import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Rocket, Navigation, Phone, Fuel, Gauge, MapPin, CheckCircle2, Radio, ShieldAlert, Timer, Siren } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const R = 3958.8;
function haversine(a, b) {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

export default function LiveRollout({ job, onChange }) {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [st, setSt] = useState(null);
  const [manualMiles, setManualMiles] = useState("");
  const [tracking, setTracking] = useState(false);
  const [liveMiles, setLiveMiles] = useState(0);
  const [price, setPrice] = useState("");
  const [mpg, setMpg] = useState("");
  const watchRef = useRef(null);
  const lastRef = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/jobs/${job.id}/rollout`);
    setSt(data); setPrice(String(data.fuel_price)); setMpg(String(data.default_mpg ?? data.mpg));
  };
  useEffect(() => { load().catch(() => {}); return () => stopTrack(); }, [job.id, job.status, job.vehicle_id, job.travel_miles]);

  const rolledOut = job.status === "en_route" || job.status === "in_progress" || st?.rolled_out;

  // Anti-forgetting: if not started yet but the crew is clearly moving, nudge them once.
  const remindAcc = useRef(0);
  const remindLast = useRef(null);
  useEffect(() => {
    if (rolledOut || !navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition((p) => {
      const cur = { lat: p.coords.latitude, lng: p.coords.longitude };
      if (remindLast.current) { const d = haversine(remindLast.current, cur); if (d > 0.005 && d < 2) remindAcc.current += d; }
      remindLast.current = cur;
      if (remindAcc.current > 0.3) {
        navigator.geolocation.clearWatch(id);
        api.post(`/jobs/${job.id}/rollout-reminder`).catch(() => {});
        toast.warning("You're on the move - tap Start Job so mileage is tracked");
      }
    }, () => {}, { enableHighAccuracy: true, maximumAge: 5000 });
    return () => navigator.geolocation.clearWatch(id);
  }, [rolledOut, job.id]);

  const rollout = async () => {
    const send = (lat, lng) => api.post(`/jobs/${job.id}/rollout`, { lat, lng })
      .then(() => { toast.success("Rolled out - en route"); onChange?.(); load(); })
      .catch((e) => toast.error(apiErr(e.response?.data?.detail)));
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => send(p.coords.latitude, p.coords.longitude), () => send(null, null), { timeout: 6000 });
    } else send(null, null);
  };

  const logMiles = async (miles, verified = false) => {
    if (!miles || miles <= 0) return;
    try {
      const { data } = await api.post(`/jobs/${job.id}/mileage`, { miles: Number(miles), verified });
      toast.success(`Logged ${miles} mi · fuel ${money(data.fuel_cost)}`);
      setManualMiles(""); setLiveMiles(0); onChange?.(); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const stopTrack = () => { if (watchRef.current != null) { navigator.geolocation.clearWatch(watchRef.current); watchRef.current = null; } lastRef.current = null; };
  const toggleTrack = () => {
    if (tracking) {
      stopTrack(); setTracking(false);
      if (liveMiles > 0.05) logMiles(Math.round(liveMiles * 10) / 10);
      return;
    }
    if (!navigator.geolocation) return toast.error("Geolocation unavailable on this device");
    setTracking(true); setLiveMiles(0);
    watchRef.current = navigator.geolocation.watchPosition((p) => {
      const cur = { lat: p.coords.latitude, lng: p.coords.longitude };
      if (lastRef.current) { const d = haversine(lastRef.current, cur); if (d > 0.005 && d < 2) setLiveMiles((m) => m + d); }
      lastRef.current = cur;
    }, () => {}, { enableHighAccuracy: true, maximumAge: 5000 });
  };

  const verify = async () => { try { await api.post(`/jobs/${job.id}/mileage/verify`); toast.success("Mileage verified"); onChange?.(); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const saveFuel = async () => { try { await api.put("/workspace/fuel-settings", { fuel_price_per_gallon: Number(price), default_mpg: Number(mpg) }); toast.success("Fuel settings saved"); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  const mapUrl = st?.property_address ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(st.property_address)}` : null;
  const telUrl = st?.customer_phone ? `tel:${st.customer_phone}` : null;

  if (!st) return null;

  return (
    <div className="card-tac mt-6 p-5" data-testid="live-rollout">
      <div className="mb-3 flex items-center gap-2 font-mono-tac text-xs uppercase tracking-widest text-slate-500"><Rocket className="h-4 w-4 text-amber-400" /> Live roll-out & field mode</div>

      {!rolledOut ? (
        <button data-testid="rollout-btn" onClick={rollout} className="btn-amber inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm">
          <Rocket className="h-4 w-4" /> Start job / Roll out
        </button>
      ) : (
        <>
          <div className="flex flex-wrap gap-2" data-testid="rollout-active">
            <a href={mapUrl || "#"} target="_blank" rel="noreferrer" data-testid="open-route-btn"
              className={`inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-sm text-white hover:border-cyan-500 ${!mapUrl ? "pointer-events-none opacity-40" : ""}`}>
              <Navigation className="h-4 w-4 text-cyan-400" /> Open route
            </a>
            <a href={telUrl || "#"} data-testid="call-customer-btn"
              className={`inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-sm text-white hover:border-emerald-500 ${!telUrl ? "pointer-events-none opacity-40" : ""}`}>
              <Phone className="h-4 w-4 text-emerald-400" /> Call customer
            </a>
            <button data-testid="track-toggle-btn" onClick={toggleTrack}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm ${tracking ? "bg-rose-500/15 text-rose-300 ring-1 ring-rose-600/40" : "border border-slate-700 text-white hover:border-amber-500"}`}>
              <Radio className={`h-4 w-4 ${tracking ? "animate-pulse text-rose-400" : "text-amber-400"}`} /> {tracking ? `Tracking… ${liveMiles.toFixed(1)} mi (stop & log)` : "Auto-track GPS miles"}
            </button>
          </div>

          {st.property_address && <div className="mt-2 inline-flex items-center gap-1 text-xs text-slate-400"><MapPin className="h-3.5 w-3.5" /> {st.property_address}</div>}

          {/* Fuel overhead */}
          <div className="mt-4 grid gap-3 sm:grid-cols-4" data-testid="fuel-overhead">
            <div className="rounded-xl border border-slate-800 bg-[#0d1420] p-3">
              <div className="font-mono-tac text-[10px] uppercase text-slate-500">Miles traveled</div>
              <div className="mt-1 font-display text-xl font-black text-white" data-testid="rollout-miles">{st.travel_miles}</div>
            </div>
            <div className="rounded-xl border border-slate-800 bg-[#0d1420] p-3">
              <div className="font-mono-tac text-[10px] uppercase text-slate-500">Vehicle MPG</div>
              <div className="mt-1 font-display text-xl font-black text-white">{st.mpg}{st.vehicle ? "" : "*"}</div>
            </div>
            <div className="rounded-xl border border-slate-800 bg-[#0d1420] p-3">
              <div className="font-mono-tac text-[10px] uppercase text-slate-500">Fuel $/gal</div>
              <div className="mt-1 font-display text-xl font-black text-white">{money(st.fuel_price)}</div>
            </div>
            <div className="rounded-xl border border-amber-600/30 bg-amber-500/5 p-3">
              <div className="font-mono-tac text-[10px] uppercase text-amber-300">Fuel overhead</div>
              <div className="mt-1 font-display text-xl font-black text-amber-300" data-testid="fuel-cost">{money(st.fuel_cost)}</div>
            </div>
          </div>
          <p className="mt-1 font-mono-tac text-[10px] text-slate-600">Fuel overhead flows into the live job-cost margin above.{!st.vehicle && " *no vehicle MPG - using workspace default"}</p>

          {/* Mileage log + verify */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <input data-testid="mileage-input" type="number" value={manualMiles} onChange={(e) => setManualMiles(e.target.value)} placeholder="Add trip miles"
              className="w-40 rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none" />
            <button data-testid="log-mileage-btn" onClick={() => logMiles(manualMiles)} className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-4 py-2 text-xs text-white hover:border-cyan-500"><Gauge className="h-3.5 w-3.5 text-cyan-400" /> Log miles</button>
            {st.mileage_verified
              ? <span data-testid="mileage-verified" className="inline-flex items-center gap-1 text-xs text-emerald-400"><CheckCircle2 className="h-3.5 w-3.5" /> Mileage verified</span>
              : (["owner", "dispatcher"].includes(user?.role) && st.travel_miles > 0 && <button data-testid="verify-mileage-btn" onClick={verify} className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/50 px-4 py-2 text-xs text-amber-300 hover:bg-amber-500/10">Verify week-end mileage</button>)}
          </div>

          {isOwner && (
            <div className="mt-4 flex flex-wrap items-end gap-3 rounded-xl border border-slate-800 bg-[#0d1420] p-3" data-testid="fuel-settings">
              <div className="flex items-center gap-1.5 font-mono-tac text-[10px] uppercase text-slate-500"><Fuel className="h-3.5 w-3.5 text-amber-400" /> Fuel settings</div>
              <label className="text-xs text-slate-400">$/gal <input data-testid="fuel-price-input" type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="ml-1 w-20 rounded border border-slate-700 bg-[#0b0f17] px-2 py-1 text-white" /></label>
              <label className="text-xs text-slate-400">default MPG <input data-testid="fuel-mpg-input" type="number" value={mpg} onChange={(e) => setMpg(e.target.value)} className="ml-1 w-20 rounded border border-slate-700 bg-[#0b0f17] px-2 py-1 text-white" /></label>
              <button data-testid="fuel-save-btn" onClick={saveFuel} className="rounded-full border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:border-amber-500">Save</button>
            </div>
          )}

          <LoneWorker jobId={job.id} />
        </>
      )}
    </div>
  );
}

function LoneWorker({ jobId }) {
  const [lw, setLw] = useState(null);
  const [interval, setIntv] = useState(30);
  const load = async () => { try { const { data } = await api.get(`/jobs/${jobId}/lone-worker`); setLw(data); } catch { /* ignore */ } };
  useEffect(() => { load(); }, [jobId]);
  const call = async (path, extra) => { try { const { data } = await api.post(`/jobs/${jobId}/lone-worker/${path}`, extra || {}); return data; } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const start = async () => { await call("start", { interval_min: Number(interval) }); toast.success(`Lone-worker timer started (${interval} min)`); load(); };
  const checkin = async () => { await call("checkin"); toast.success("Checked in - you're safe"); load(); };
  const panic = async () => { await call("panic"); toast.error("PANIC sent to office"); load(); };
  const stop = async () => { await call("stop"); toast.success("Lone-worker timer ended"); load(); };
  const active = lw?.active;
  return (
    <div className="mt-4 rounded-xl border border-slate-800 bg-[#0d1420] p-4" data-testid="lone-worker">
      <div className="mb-2 flex items-center gap-2 font-mono-tac text-[10px] uppercase tracking-widest text-slate-500"><ShieldAlert className="h-3.5 w-3.5 text-amber-400" /> Lone-worker safety</div>
      {!active ? (
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs text-slate-400 inline-flex items-center gap-1"><Timer className="h-3.5 w-3.5" /> Check-in every
            <input data-testid="lw-interval-input" type="number" value={interval} onChange={(e) => setIntv(e.target.value)} className="w-16 rounded border border-slate-700 bg-[#0b0f17] px-2 py-1 text-white" /> min</label>
          <button data-testid="lw-start-btn" onClick={start} className="btn-amber rounded-full px-4 py-1.5 text-xs">Start safety timer</button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono-tac text-xs text-slate-400" data-testid="lw-next-due">Next check-in due {lw.next_due ? new Date(lw.next_due).toLocaleTimeString() : "-"}</span>
          <button data-testid="lw-checkin-btn" onClick={checkin} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-600/50 px-4 py-1.5 text-xs text-emerald-300 hover:bg-emerald-500/10"><CheckCircle2 className="h-3.5 w-3.5" /> I'm safe (check in)</button>
          <button data-testid="lw-panic-btn" onClick={panic} className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-rose-500"><Siren className="h-3.5 w-3.5" /> Panic</button>
          <button data-testid="lw-stop-btn" onClick={stop} className="rounded-full border border-slate-700 px-3 py-1.5 text-xs text-slate-400 hover:border-slate-500">End</button>
          {lw.panic && <span className="font-mono-tac text-xs text-rose-400">PANIC active</span>}
        </div>
      )}
    </div>
  );
}
